<template>
  <div class="w-full h-full overflow-y-scroll">
    <div v-if="automation?.steps" class="new-automation px-4 pt-4 h-full">
      <span
        class="text-lg font-medium w-full text-center flex">עריכת אוטומציה</span>
      <span> {{ automation?.name }}</span>

      <div class="flex flex-col w-full h-max flex-grow items-center mt-4">
        <div v-for="(step, idx) of automation?.steps ?? []" class=" w-full">
          <BeginWebNode v-if="step.action === SmsAutomationActions.BeginWeb" :data="step" @remove="removeStep(idx)"/>
          <BeginScheduledNode
            v-if="step.action === SmsAutomationActions.BeginScheduled"
            :data="step"
            @remove="removeStep(idx)"/>

          <LeftScheduleNode
            v-if="step.action === SmsAutomationActions.LeftSchedule"
            :data="step"
            @remove="removeStep(idx)"/>
          <CustomerCanceledNode
            v-if="step.action === SmsAutomationActions.CustomerCanceled"
            :data="step"
            @remove="removeStep(idx)"/>
          <CustomerDidntVisit
            v-if="step.action === SmsAutomationActions.CustomerDidntVisit"
            :data="step"
            @remove="removeStep(idx)"/>
          <FinishedScheduleNode
            v-if="step.action === SmsAutomationActions.FinishedSchedule"
            :data="step"
            @remove="removeStep(idx)"/>
          <AmountOfDaysPassedNode
            v-if="step.action === SmsAutomationActions.AmountOfDaysPassed"
            :data="step"
            @remove="removeStep(idx)"
            @update-amount-of-days="saveDaysPassed"/>

          <SmsNode
            v-if="step.action === SmsAutomationActions.SendSMs"
            :data="step"
            @remove="removeStep(idx)"
            @update-sms-message="saveMessageContent"/>
          <div v-if="idx !== 2" class="w-full h-8 flex justify-center items-center">
            <div class="h-full w-[1px] border border-solid border-gray-300"/>
          </div>
        </div>

        <NewNodeButton
          v-if="automation?.steps?.length <= 2"
          @click="openStepPicker(SideBarPage.NewStep)"/>

        <div class="flex flex-row gap-3 w-full mt-8 mb-4">
          <Button
            class="lead-color-button w-full"
            style="display: flex; flex-grow: 1"
            label=" שמירת שינויים "
            @click="saveAutomation"/>
          <Button
            class="w-full"
            style="display: flex; flex-grow: 1"
            label=" ביטול "
            outlined
            @click="cancel"/>
        </div>
        <Divider align="center">
          <b>אפשרויות נוספות</b>
        </Divider>
        <Button
          label="מחיקת אוטומציה"
          class="p-button-danger w-full flex-none mt-2 mb-4"
          iconPos="right"
          icon="pi pi-trash"
          @click="removeAutomation"/>
      </div>
    </div>


    <ConfirmDialog/>
  </div>
</template>

<script lang="ts" setup>
import {computed, onMounted, ref} from "vue";
import {useToast} from "vue-toastification";
import {useConfirm} from "primevue/useconfirm";
import {useDrawer} from "@torim-app/shared-components";
import AutomationStepPicker from '@app/components/admin/AutomationStepPicker.vue';
import {ISmsAutomation, SmsAutomationActions} from "@torim-app/types";
import {automationActionsTree, automationsStarts, triggersMapping} from "./pages/communication/automations-consts";
import BeginWebNode from './pages/communication/custom-nodes/BeginWebNode.vue';
import CustomerCanceledNode from './pages/communication/custom-nodes/CustomerCanceledNode.vue';
import BeginScheduledNode from './pages/communication/custom-nodes/BeginScheduledNode.vue';
import FinishedScheduleNode from './pages/communication/custom-nodes/FinishedScheduleNode.vue';
import SmsNode from './pages/communication/custom-nodes/SmsNode.vue';
import LeftScheduleNode from './pages/communication/custom-nodes/LeftScheduleNode.vue';
import CustomerDidntVisit from './pages/communication/custom-nodes/CustomerDidntVisit.vue';
import {CommunicationApi} from "@app/api/admin";
import {useAdminAutomations, useAdminStore} from "@app/stores";
import {useRoute} from "vue-router";
import {AdminRouteNames} from "@app/router/route-names";
import {useNavigation} from "@app/composables/navigation";
import {DateTime} from "luxon";
import ConfirmDialog from 'primevue/confirmdialog';
import { trackEvent } from '@app/composables/posthog';

enum SideBarPage {
  NewStep = 'new-step',
  Init = 'init',
}

const confirm = useConfirm();
const toast = useToast();
const adminStore = useAdminStore();
const route = useRoute();
const adminAutomations = useAdminAutomations();
const {goTo} = useNavigation();
const {openDrawer} = useDrawer();

const automationId = route.params.id as string;

const automation = ref<ISmsAutomation>(automationId === 'new' ?
  <ISmsAutomation>{name: `אוטומציה חדשה ${DateTime.local().toLocaleString(DateTime.DATETIME_MED)}`} : <ISmsAutomation>{});

const selectedAutomation = computed(() => {
  return adminAutomations.getAutomations.find(x => x.id === automationId) ?? <ISmsAutomation>{}
})

const onSidebarHidden = () => {
  if (automationId === 'new' && !automation.value?.steps)
    goTo(AdminRouteNames.Communication);
};

const openStepPicker = (page: SideBarPage) => {
  openDrawer(AutomationStepPicker, {
    sideBarPage: page,
    automation: automation.value,
    automationId,
    onAddStep: addStep,
    onSidebarHidden
  }, { title: 'יצירת אוטומציה חדשה' });
};

const cancel = () => {
  goTo(AdminRouteNames.Communication);
}

const removeAutomation = () => {
  confirm.require({
    message: '⚠ האם למחוק את ההודעה האוטומטית הנוכחית? לא ניתן לבטל פעולה זו',
    header: 'רק מוודאים',
    rejectLabel: 'לא',
    acceptLabel: 'כן',
    blockScroll: true,
    accept: async () => {
      try {
        if (selectedAutomation.value.id)
          await CommunicationApi.removeAutomations(selectedAutomation.value.id);
        trackEvent('automation_deleted');
        toast.success('מחקנו בהצלחה, לא יישלחו עוד הודעות');
        goTo(AdminRouteNames.Communication, {method: "replace"});
      } catch (e) {
        console.error(e)
        toast.error("לא הצלחנו למחוק את ההודעה האוטומטית, אנא נסו שוב");
      }
    },
  });
}

const saveAutomation = () => {
  trackEvent('automation_saved');
  const steps = automation.value?.steps ?? []
  if (steps.length !== 3) {
    toast.error('לא ניתן לשמור הודעות אוטומטיות חלקיות, יש למלא את כל השלבים!')
    return;
  }

  if (steps[2].action === SmsAutomationActions.SendSMs && !steps[2]?.additionalParams?.message) {
    toast.error('לא ניתן לשלוח הודעה ללא תוכן!')
    return;
  }

  const doc: ISmsAutomation = {
    name: automation.value.name,
    bid: adminStore.getBusinessSiteData.bid,
    id: selectedAutomation.value?.id,
    steps,
  }

  CommunicationApi.saveAutomation(doc)
    .then(_ => {
      toast.success(selectedAutomation.value ? 'האוטומציה עודכנה בהצלחה' : 'האוטומציה נוצרה בהצלחה');
      goTo(AdminRouteNames.Communication);
    })
    .catch(e => {
      console.error(e);
      toast.error(selectedAutomation.value ? 'לא הצלחנו לעדכן את האוטומציה' : 'לא הצלחנו ליצור את האוטומציה');
    })
}

const removeStep = (stepIdx: number) => {
  automation.value.steps.splice(stepIdx);
}

const addStep = (action: SmsAutomationActions) => {
  if (automationId === 'new' && !automation.value.steps)
    automation.value.steps = [];

  automation.value.steps.push({
    action,
    additionalParams: [SmsAutomationActions.SendSMs, SmsAutomationActions.AmountOfDaysPassed].includes(action) ? {} : undefined
  });
}

const saveMessageContent = (message: string) => {
  const relevantElement = automation.value.steps.find(elem => elem?.action === SmsAutomationActions.SendSMs);
  if (relevantElement?.additionalParams) {
    relevantElement.additionalParams.message = message;
  }
}

const saveDaysPassed = (days: number) => {
  const relevantElement = automation.value.steps.find(elem => elem?.action === SmsAutomationActions.AmountOfDaysPassed);
  if (relevantElement) {
    if (!relevantElement?.additionalParams) relevantElement.additionalParams = {};
    relevantElement.additionalParams.days = days;
  }

}

onMounted(async () => {
  trackEvent('automation_viewed', { is_new: automationId === 'new' });
  if (!adminAutomations.getAutomations.length) {
    await adminAutomations.fetchAllAutomations();
  }

  if (automationId !== 'new') {
    automation.value = adminAutomations.getAutomations.find(x => x.id === automationId)!;
  } else {
    openStepPicker(SideBarPage.Init);
  }
});
</script>

<style lang="scss">
@use '../../../assets/css/vars' as *;

.new-automation {
  width: 100%;
  height: 100%;
}

.node-menu {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  font-weight: 500;
  font-size: 1.05rem;
  gap: 1rem;

  .row {
    display: flex;
    justify-content: flex-start;
    align-items: center;

    i {
      margin-left: 0.5rem;
    }

    &.remove {
      color: red;
    }
  }

  .replace-menu {
    border-radius: 0.5rem;
    display: flex;
    flex-direction: column;
    color: black;

    span {
      margin-right: 1.3rem;
      margin-top: 0.25rem;
    }

    span:nth-child(2) {
      font-size: 1rem;
      font-weight: 400;
    }
  }

  .intro {
    font-size: 0.85rem;
    padding: 0.75rem;
    background-color: $gray-bg;
    border-radius: 0.4rem;
    margin-bottom: 0.5rem;
    font-weight: 400;
  }

}
</style>
